import { reportRequest } from "./reportRequest";
import { ApiBase } from "./ApiBase";
import type {
  ReportData,
  SpecChartPanel,
} from "@/components/reports/presentation/utils/types";

export type Measure =
  | "recommendedAnnualRent"
  | "buildingAnnualRent"
  | "areaAnnualRent"
  | "recommendedSamples"
  | "buildingSamples"
  | "areaSamples"
  | "medianAnnualRent"
  | "transactionCount";
export interface RentalMarketScope {
  kind: "project" | "community";
  community: string;
  propertyType: "Villa" | "Unit";
  project?: string | null;
}
export interface ViewDataset {
  id: string;
  provider:
    | "building-rent-v1"
    | "rental-benchmark-v2"
    | "rental-transactions-v1"
    | "rental-project-benchmark-v1"
    | "rental-market-transactions-v1"
    | "captured-dataset-v1";
  parameters: {
    buildingGid?: string | null;
    sizeSqm: number;
    months?: number;
    scope?: RentalMarketScope;
    recipeId?: string;
    columns?: string[];
  };
}
export interface ViewWidget {
  id: string;
  datasetId: string;
  kind: "metric" | "bar" | "table" | "trend" | "transactions" | "dataset";
  display?: {
    title: string;
    x?: string;
    y?: string;
    chart?: "bar" | "line" | "table" | "spec";
  };
  compareWith?: string[];
  width?: "full" | "half";
  measures: Measure[];
}
export interface ViewDefinition {
  version: 1 | 2;
  title: string;
  datasets: ViewDataset[];
  widgets: ViewWidget[];
}
export interface DatasetResult {
  id: string;
  status: "ok" | "empty" | "limited";
  buildingGid?: string | null;
  scope?: RentalMarketScope;
  sizeSqm: number;
  subject: string;
  sourceDate: string | null;
  sourceGrain: string | null;
  sourceWindow: string | null;
  quality: string | null;
  measures: Partial<Record<Measure, number | null>>;
  recipeId?: string;
  data?: ReportSnapshot;
  chart?: SpecChartPanel;
  matchedSizeSqm?: number | null;
  rows?: DataPoint[];
  series?: DataPoint[];
  /** What the dataset covers, worked out by bondoq's capture from the query
   *  it came from, on every run (captured data only; absent before then). */
  facts?: DatasetFacts | null;
}
/** A captured dataset's facts. Each is left out when not known; the report
 *  read omits an absent one and the view read writes it as null. Area and
 *  buildings are never both present. */
export interface DatasetFacts {
  area?: string | null;
  buildings?: string | null;
  period?: string | null;
  sample?: string | null;
}
export interface DataPoint {
  date: string;
  annualRent: number;
  sizeSqm: number;
  count: number;
}
export interface ReportSnapshot {
  title: string;
  columns: { key: string; label: string; unit?: string }[];
  rows: Record<string, string | number | boolean | null>[];
  source: string;
  capturedAt: string;
  totalRows: number;
  x?: string;
  y?: string;
}
export type ReportBlock =
  | { kind: "snapshot"; snapshot: ReportSnapshot }
  | { kind: "narrative"; markdown: string }
  | { kind: "data"; widgetId: string }
  | {
      kind: "citation";
      title: string;
      reference: string;
      excerpt: string;
      url?: string;
    };
export interface ReportSection {
  id: string;
  title: string;
  blocks: ReportBlock[];
}
export interface ReportComposition {
  version: 2;
  title: string;
  subject: string;
  summary: string;
  analysisDate: string;
  category: "market" | "legal";
  sections: ReportSection[];
}
export interface ViewObservation {
  definitionVersion: number;
  executedAt: string;
  datasets: DatasetResult[];
}
export interface ReportSummary {
  id: string;
  title: string;
  category: "market" | "legal";
  publishedAt: string;
  trackable: boolean;
}
export interface AppReport extends ReportSummary {
  canTrack?: boolean;
  hasAccount?: boolean;
  pdfAvailable: boolean;
  content: (ReportData | ReportComposition) & {
    viewDefinition?: ViewDefinition;
    viewObservation?: ViewObservation;
  };
}
export interface ReportLibraryQuery {
  archived?: boolean;
  category?: "market" | "legal";
  search?: string;
  page?: number;
  pageSize?: number;
}
export interface ReportLibraryPage {
  items: ReportSummary[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}
export interface ViewSummary {
  id: string;
  title: string;
  sourceReportId: string;
  lastAttemptAt: string | null;
  refreshError: string | null;
  nextRefreshAt: string;
  compositionVersion?: number;
  componentCount?: number;
  subjects?: string[];
  preview?: {
    measure: Measure | null;
    label?: string;
    rowCount?: number;
    value: number | null;
    sourceDate: string | null;
    executedAt: string;
    status: string;
  } | null;
}
export interface SavedView extends ViewSummary {
  /** The source report's published date (view read only; absent from older
   *  Core builds). */
  sourceReportPublishedAt?: string | null;
  version: number;
  definition: ViewDefinition;
  observation: ViewObservation | null;
  isFollowing: boolean;
  refreshing: boolean;
  history: {
    id: string;
    definitionVersion: number;
    createdAt: string;
    data: ViewObservation;
    definition: ViewDefinition;
  }[];
}

class ReportsApi extends ApiBase {
  list(signal?: AbortSignal) {
    return this.get<ReportSummary[]>("/reports", { signal });
  }
  library(params: ReportLibraryQuery, signal?: AbortSignal) {
    return this.get<ReportLibraryPage>("/reports/library", { params, signal });
  }
  archive(id: string) {
    return this.put(`/reports/${id}/archive`, {});
  }
  restore(id: string) {
    return this.delete(`/reports/${id}/archive`);
  }
  report(id: string, signal?: AbortSignal) {
    return reportRequest<AppReport>(`/reports/${id}`, signal);
  }
  pdf(id: string) {
    return reportRequest<Blob>(`/reports/${id}/pdf`, undefined, true);
  }
  views(signal?: AbortSignal) {
    return this.get<ViewSummary[]>("/views", { signal });
  }
  /** A saved view. `history` asks Core for fewer past observations (each
   *  carries its full data); without it Core answers its 20 newest. */
  view(id: string, signal?: AbortSignal, options?: { history?: number }) {
    return this.get<SavedView>(`/views/${id}`, {
      signal,
      ...(options?.history !== undefined && {
        params: { history: options.history },
      }),
    });
  }
  track(
    reportId: string,
    selection?: { widgetIds: string[]; title: string; requestId: string },
  ) {
    return this.post<
      {
        reportId: string;
        widgetIds?: string[];
        title?: string;
        requestId?: string;
      },
      { id: string }
    >("/views", {
      reportId,
      ...selection,
    });
  }
  edit(id: string, version: number, definition: ViewDefinition) {
    return this.put(`/views/${id}`, { version, definition });
  }
  addSections(
    id: string,
    version: number,
    reportId: string,
    widgetIds: string[],
  ) {
    return this.post(`/views/${id}/sections`, {
      version,
      reportId,
      widgetIds,
    });
  }
  refresh(id: string) {
    return this.post(`/views/${id}/refresh`, {});
  }
  unfollow(id: string) {
    return this.delete(`/views/${id}`);
  }
}
export const reportsApi = new ReportsApi();
